import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Box from '@mui/material/Box';
import {
  fulfillmentOptions,
  ALL_FULFILLMENT_METHODS
} from 'constants/fulfillmentOptions';
import { promotionTypes } from 'constants/promotions';
import { PROMOTION_PROPTYPES } from 'propTypes';

import SecondaryCta from 'components/Buttons/SecondaryCta';
import { StyledTypographyColorSectionNavLinkMainMedium } from 'components/StyledTypographyColorSectionNavLinkMainMedium';
import { StyledTypographyColorSectionNavLinkMain } from 'components/Cart/StyledTypographyColorSectionNavLinkMain';
import { getValueWithTwoDecimalPlace } from 'helpers/formattedDollar';
import {
  ITEM_DESCRIPTION_AND_PRICES,
  MENU_ITEM_NAMES
} from 'styles/constants/appearanceSectionNames';
import { getOwnCartDrawerState } from 'styles/helpers/getSecondaryButtonState';
import { EMPTY_CART_CONFIRMATION_MODAL } from 'constants/cart';
import CartInfoTaxesAndFees from 'catering/components/Cart/CartInfoTaxesAndFees';

const flexProps = {
  justifyContent: 'space-between',
  display: 'flex'
};
function DeliveryFee({
  deliveryFee,
  fulfillmentMethod,
  isFreeDeliveryPromotion,
  ...cartInfoStyles
}) {
  if (fulfillmentMethod !== fulfillmentOptions.DELIVERY) {
    return null;
  }

  const hasDeliveryFee = parseFloat(deliveryFee) > 0;
  let value = '';

  if (hasDeliveryFee && isFreeDeliveryPromotion) {
    value = (
      <>
        <StyledTypographyColorSectionNavLinkMainMedium
          component="span"
          sx={{ textDecoration: 'line-through' }}
        >
          ${deliveryFee}
        </StyledTypographyColorSectionNavLinkMainMedium>{' '}
        Free
      </>
    );
  } else if (hasDeliveryFee) {
    value = `$${deliveryFee}`;
  } else {
    value = 'Free';
  }

  return (
    <Box
      data-testid="cart-delivery-fee-section"
      marginBottom={1}
      {...flexProps}
    >
      <StyledTypographyColorSectionNavLinkMainMedium {...cartInfoStyles}>
        Delivery
      </StyledTypographyColorSectionNavLinkMainMedium>
      <StyledTypographyColorSectionNavLinkMainMedium
        data-testid="cart-delivery-fee"
        {...cartInfoStyles}
      >
        {value}
      </StyledTypographyColorSectionNavLinkMainMedium>
    </Box>
  );
}

DeliveryFee.propTypes = {
  deliveryFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fulfillmentMethod: PropTypes.oneOf(ALL_FULFILLMENT_METHODS),
  isFreeDeliveryPromotion: PropTypes.bool,
  cartInfoStyles: PropTypes.object
};

function PickupFee({ pickupFee, fulfillmentMethod, ...cartInfoStyles }) {
  if (fulfillmentMethod !== fulfillmentOptions.PICKUP) {
    return null;
  }

  const pickupFeeText =
    parseFloat(pickupFee) > 0
      ? `$${getValueWithTwoDecimalPlace(pickupFee)}`
      : 'Free';

  return (
    <Box data-testid="cart-pickup-fee-section" marginBottom={1} {...flexProps}>
      <StyledTypographyColorSectionNavLinkMainMedium {...cartInfoStyles}>
        Pickup
      </StyledTypographyColorSectionNavLinkMainMedium>
      <StyledTypographyColorSectionNavLinkMainMedium
        data-testid="cart-pickup-fee"
        {...cartInfoStyles}
      >
        {pickupFeeText}
      </StyledTypographyColorSectionNavLinkMainMedium>
    </Box>
  );
}

PickupFee.propTypes = {
  pickupFee: PropTypes.number,
  fulfillmentMethod: PropTypes.oneOf(ALL_FULFILLMENT_METHODS),
  cartInfoStyles: PropTypes.object
};

function CartInfo({
  bundleServiceFeeWithCustomFee,
  bundledServiceAndCustomFees,
  toggleIsOpen,
  covidFee,
  customFee,
  customFeeName,
  deliveryFee,
  digitalGiftCardAmountApplied,
  fulfillmentMethod,
  pickupFee,
  promotion,
  serviceFee,
  subtotal,
  tax,
  taxesAndFees,
  taxFieldLabel,
  tipAmount,
  totalOwed,
  shouldShowTransparentFees,
  displayServiceFee
}) {
  const digitalGiftCardAmountAppliedPrice = getValueWithTwoDecimalPlace(
    digitalGiftCardAmountApplied
  );
  const covidFeeFormatted = getValueWithTwoDecimalPlace(covidFee);
  const tipAmountFormatted = getValueWithTwoDecimalPlace(tipAmount);
  const totalOwedFormatted = getValueWithTwoDecimalPlace(totalOwed);
  const deliveryFeeFormatted = getValueWithTwoDecimalPlace(deliveryFee);
  const promotionType = get(promotion, 'promotion_type', '');
  const isFreeDeliveryPromotion =
    promotionType === promotionTypes.FREE_DELIVERY;

  const cartInfoStyles = { variant: ITEM_DESCRIPTION_AND_PRICES };

  const totalStyles = { variant: MENU_ITEM_NAMES };

  return (
    <Box marginTop={2} marginBottom={2}>
      <Box marginBottom={1} {...flexProps}>
        <StyledTypographyColorSectionNavLinkMainMedium {...cartInfoStyles}>
          Subtotal
        </StyledTypographyColorSectionNavLinkMainMedium>
        <StyledTypographyColorSectionNavLinkMainMedium
          data-testid="cart-subtotal"
          {...cartInfoStyles}
        >
          ${subtotal}
        </StyledTypographyColorSectionNavLinkMainMedium>
      </Box>
      <CartInfoTaxesAndFees
        shouldShowTransparentFees={shouldShowTransparentFees}
        taxFieldLabel={taxFieldLabel}
        taxesAndFees={taxesAndFees}
        tax={tax}
        serviceFee={serviceFee}
        customFee={customFee}
        customFeeName={customFeeName}
        bundleServiceFeeWithCustomFee={bundleServiceFeeWithCustomFee}
        bundledServiceAndCustomFees={bundledServiceAndCustomFees}
        displayServiceFee={displayServiceFee}
        {...cartInfoStyles}
      />
      {!!Number(tipAmount) && (
        <Box marginBottom={1} {...flexProps}>
          <StyledTypographyColorSectionNavLinkMainMedium {...cartInfoStyles}>
            Tip Amount
          </StyledTypographyColorSectionNavLinkMainMedium>
          <StyledTypographyColorSectionNavLinkMainMedium
            data-testid="tip-amount"
            {...cartInfoStyles}
          >
            ${tipAmountFormatted}
          </StyledTypographyColorSectionNavLinkMainMedium>
        </Box>
      )}
      {!!covidFee && (
        <Box marginBottom={1} {...flexProps}>
          <StyledTypographyColorSectionNavLinkMainMedium {...cartInfoStyles}>
            Covid Fee
          </StyledTypographyColorSectionNavLinkMainMedium>
          <StyledTypographyColorSectionNavLinkMainMedium
            data-testid="covid-fee"
            {...cartInfoStyles}
          >
            ${covidFeeFormatted}
          </StyledTypographyColorSectionNavLinkMainMedium>
        </Box>
      )}
      {!!digitalGiftCardAmountApplied && (
        <Box marginBottom={1} {...flexProps}>
          <StyledTypographyColorSectionNavLinkMainMedium {...cartInfoStyles}>
            Gift Card
          </StyledTypographyColorSectionNavLinkMainMedium>
          <StyledTypographyColorSectionNavLinkMainMedium
            data-testid="cart-digital-gift-card-amount-applied"
            {...cartInfoStyles}
          >
            -${digitalGiftCardAmountAppliedPrice}
          </StyledTypographyColorSectionNavLinkMainMedium>
        </Box>
      )}
      <DeliveryFee
        deliveryFee={deliveryFeeFormatted}
        fulfillmentMethod={fulfillmentMethod}
        isFreeDeliveryPromotion={isFreeDeliveryPromotion}
        {...cartInfoStyles}
      />
      <PickupFee
        pickupFee={pickupFee}
        fulfillmentMethod={fulfillmentMethod}
        {...cartInfoStyles}
      />
      <Box marginBottom={2} {...flexProps}>
        {shouldShowTransparentFees ? (
          <Box display="flex" alignItems="center">
            <StyledTypographyColorSectionNavLinkMain
              {...totalStyles}
              fontSize="16px"
            >
              Cart Subtotal
            </StyledTypographyColorSectionNavLinkMain>
          </Box>
        ) : (
          <StyledTypographyColorSectionNavLinkMain
            {...totalStyles}
            fontSize="16px"
          >
            Total
          </StyledTypographyColorSectionNavLinkMain>
        )}
        <StyledTypographyColorSectionNavLinkMain
          {...totalStyles}
          fontSize="16px"
          data-testid="cart-total"
        >
          ${totalOwedFormatted}
        </StyledTypographyColorSectionNavLinkMain>
      </Box>
      <SecondaryCta
        handleClick={() => toggleIsOpen(EMPTY_CART_CONFIRMATION_MODAL)}
        label="Empty Cart"
        dataTestId="cart-empty-button"
        ownState={getOwnCartDrawerState()}
      />
    </Box>
  );
}

CartInfo.propTypes = {
  bundleServiceFeeWithCustomFee: PropTypes.bool,
  bundledServiceAndCustomFees: PropTypes.string,
  toggleIsOpen: PropTypes.func,
  covidFee: PropTypes.number,
  customFee: PropTypes.string,
  customFeeName: PropTypes.string,
  deliveryFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  digitalGiftCardAmountApplied: PropTypes.number,
  fulfillmentMethod: PropTypes.oneOf(ALL_FULFILLMENT_METHODS),
  pickupFee: PropTypes.number,
  promotion: PROMOTION_PROPTYPES,
  serviceFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subtotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  taxesAndFees: PropTypes.string,
  taxFieldLabel: PropTypes.string,
  tipAmount: PropTypes.string,
  totalOwed: PropTypes.number.isRequired,
  shouldShowTransparentFees: PropTypes.bool.isRequired,
  displayServiceFee: PropTypes.string.isRequired
};

export default CartInfo;
